// Smart Sash Admin Panel - RFID Authentication Service
// Handles RFID-based authentication for registration flow

import {
  AssignRFIDCardRequest,
  RFIDAssignmentRequest,
  RFIDAssignmentRequestData,
  RFIDAuthRequest,
  RFIDAuthResponse,
  RevokeAssignedCardRequest,
} from '@/models';
import { axiosClient } from '../http/axiosClient';

const RFID_AUTH_ENDPOINTS = {
  AUTHENTICATE: '/rfidAssignment/authenticate',
  REQUEST: '/rfidAssignment/request',
  REVOKE: '/rfidAssignment/revoke',
  ASSIGN: '/rfidAssignment/assign',
} as const;

export interface RFIDAuthenticateResult {
  response: RFIDAuthResponse;
  requiresPasswordChange: boolean;
}

export const rfidAuthService = {
  /**
   * Authenticate user credentials for RFID assignment flow
   */
  authenticate: async (request: RFIDAuthRequest): Promise<RFIDAuthenticateResult> => {
    const response = await axiosClient.post<RFIDAuthResponse>(
      RFID_AUTH_ENDPOINTS.AUTHENTICATE,
      request,
      {
        headers: { Authorization: undefined },
        validateStatus: (status) => status === 200 || status === 301,
      }
    );

    return {
      response: response.data,
      requiresPasswordChange: response.status === 301,
    };
  },

  /**
   * Trigger an RFID assignment request (sends VerificationCodeSentEvent via gRPC)
   */
  requestAssignment: async (request: RFIDAssignmentRequest): Promise<RFIDAssignmentRequestData> => {
    const response = await axiosClient.post<string>(RFID_AUTH_ENDPOINTS.REQUEST, request);

    return {
      success: true,
      expiresAt: response.data,
    };
  },

  /**
   * Revoke a pending RFID assignment request
   */
  revokeAssignment: async (request: RFIDAssignmentRequest): Promise<void> => {
    await axiosClient.post(RFID_AUTH_ENDPOINTS.REVOKE, request);
  },

  revokeAssignedCard: async (request: RevokeAssignedCardRequest): Promise<void> => {
    await axiosClient.post(RFID_AUTH_ENDPOINTS.REVOKE, request);
  },

  assignCard: async (request: AssignRFIDCardRequest): Promise<string> => {
    const response = await axiosClient.post<string>(RFID_AUTH_ENDPOINTS.ASSIGN, request);
    return response.data;
  },
};

export default rfidAuthService;
